import React from 'react';
import {
  BarChart2,
  TrendingUp,
  PieChart,
  Layers,
  Eye,
  EyeOff,
  Download,
  Image,
} from 'lucide-react';
import { ChartType, DatasetEntry } from '../../utils/dataDetector';
import { TaskySelect } from '../tasky';

interface ChartControlsProps {
  chartType: ChartType;
  onChartTypeChange: (type: ChartType) => void;
  showLegend: boolean;
  onToggleLegend: () => void;
  datasets: DatasetEntry[];
  selectedDatasetIndex: number | null;
  onSelectDataset: (index: number | null) => void;
  onExportPng: () => void;
  onExportCsv: () => void;
}

const CHART_TYPES: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'bar', icon: <BarChart2 size={14} />, label: '棒グラフ' },
  { type: 'line', icon: <TrendingUp size={14} />, label: '折れ線' },
  { type: 'pie', icon: <PieChart size={14} />, label: '円グラフ' },
  { type: 'stacked_bar', icon: <Layers size={14} />, label: '積上げ' },
];

// ChatDataChart.scss から内製化（Wave 3d / T20-batch3a）。
// active 状態は SCSS 上で `:hover` より後に定義されており同一詳細度のため
// hover 中も active の色が勝つ。したがって active 側に hover 上書きは付けない。
const CHART_TYPE_BTN_INACTIVE =
  'flex items-center justify-center w-7 h-7 border-none rounded cursor-pointer transition-all duration-150 ease-[ease] bg-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50';
const CHART_TYPE_BTN_ACTIVE =
  'flex items-center justify-center w-7 h-7 border-none rounded cursor-pointer transition-all duration-150 ease-[ease] bg-white text-[#4965ff] shadow-[0_1px_2px_rgba(0,0,0,0.05)]';

const LEGEND_BTN_INACTIVE =
  'flex items-center justify-center w-7 h-7 rounded-md cursor-pointer transition-all duration-150 ease-[ease] border border-gray-300 bg-white text-gray-500 hover:text-gray-700 hover:border-gray-400';
const LEGEND_BTN_ACTIVE =
  'flex items-center justify-center w-7 h-7 rounded-md cursor-pointer transition-all duration-150 ease-[ease] border border-[#4965ff] bg-[#4965ff]/[0.05] text-[#4965ff]';
const EXPORT_BTN =
  'flex items-center justify-center w-7 h-7 rounded-md cursor-pointer transition-all duration-150 ease-[ease] border border-gray-300 bg-white text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-gray-100';

const ChartControls: React.FC<ChartControlsProps> = ({
  chartType,
  onChartTypeChange,
  showLegend,
  onToggleLegend,
  datasets,
  selectedDatasetIndex,
  onSelectDataset,
  onExportPng,
  onExportCsv,
}) => {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-b-gray-200 gap-2">
      <div className="flex items-center gap-2">
        {/* Chart type selector */}
        <div className="flex gap-[2px] bg-gray-200 rounded-md p-[2px]">
          {CHART_TYPES.map(({ type, icon, label }) => (
            <button
              key={type}
              className={chartType === type ? CHART_TYPE_BTN_ACTIVE : CHART_TYPE_BTN_INACTIVE}
              onClick={() => onChartTypeChange(type)}
              title={label}
            >
              {icon}
            </button>
          ))}
        </div>

        {/* Dataset selector (when multiple datasets) */}
        {datasets.length > 1 && (
          <TaskySelect
            className="h-8 text-sm"
            value={selectedDatasetIndex ?? ''}
            onChange={e => {
              const val = e.target.value;
              onSelectDataset(val === '' ? null : Number(val));
            }}
          >
            <option value="">全指標</option>
            {datasets.map((ds, i) => (
              <option key={i} value={i}>{ds.label}</option>
            ))}
          </TaskySelect>
        )}

        {/* Legend toggle */}
        <button
          className={showLegend ? LEGEND_BTN_ACTIVE : LEGEND_BTN_INACTIVE}
          onClick={onToggleLegend}
          title={showLegend ? '凡例を非表示' : '凡例を表示'}
        >
          {showLegend ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
      </div>

      <div className="flex items-center gap-1">
        <button className={EXPORT_BTN} onClick={onExportPng} title="PNGダウンロード">
          <Image size={14} />
        </button>
        <button className={EXPORT_BTN} onClick={onExportCsv} title="CSVダウンロード">
          <Download size={14} />
        </button>
      </div>
    </div>
  );
};

export default ChartControls;
